import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LoginBranding } from '../../config';
import { useTheme } from '../../theme';
import Button from '../Button';
import Card from '../Card';
import Input from '../Input';

export interface ForgotPasswordRequestValues {
  email: string;
}

export interface ForgotPasswordResetValues {
  token: string;
  newPassword: string;
  confirmPassword: string;
}

export interface ForgotPasswordProps {
  branding: LoginBranding;
  /** Chamado ao enviar o e-mail: backend gera e envia o token */
  onRequestToken: (email: string) => Promise<void>;
  /** Chamado ao enviar token + nova senha: backend valida token e efetiva a troca */
  onResetPassword: (params: {
    token: string;
    newPassword: string;
  }) => Promise<void>;
  /** Navegação de volta para a tela de Login */
  onBackToLogin: () => void;
  /**
   * Deep link para abrir direto o passo de reset.
   * Ex.: /forgot-password?token=...
   */
  initialToken?: string;
  /**
   * Prefill do email no passo 1 (ex.: /forgot-password?email=...).
   * Útil quando o usuário digita no Login e depois clica em "forgot password".
   */
  initialEmail?: string;
}

const MIN_PASSWORD_LENGTH = 8;

export default function ForgotPassword({
  branding,
  onRequestToken,
  onResetPassword,
  onBackToLogin,
  initialToken,
  initialEmail,
}: ForgotPasswordProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const primaryColor = branding.primaryColor;

  const initialStep = initialToken?.trim() ? 'reset' : 'email';
  const initialEmailValue = initialEmail?.trim() ?? '';
  const [step, setStep] = useState<'email' | 'reset'>(initialStep);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [tokenTooltipVisible, setTokenTooltipVisible] = useState(false);

  const pageStyle: React.CSSProperties = {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: theme.palette.gradient.page,
    padding: theme.spacing.lg,
  };

  const cardContentStyle: React.CSSProperties = {
    width: theme.layout.cardWidth,
    minHeight: theme.layout.cardHeight,
    borderRadius: theme.shape.borderRadius.lg,
  };

  const formStyle: React.CSSProperties = {
    width: '100%',
    textAlign: 'left',
  };

  const logoStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamilySerif,
    fontSize: theme.typography.fontSize['3xl'],
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.palette.text.primary,
    letterSpacing: '0.02em',
    marginBottom: theme.spacing.xs,
  };

  const titleStyle: React.CSSProperties = {
    fontSize: theme.typography.fontSize.xl,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing.xs,
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: theme.typography.fontSize.md,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing.lg,
  };

  const backLinkStyle: React.CSSProperties = {
    display: 'inline-block',
    textAlign: 'center',
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.medium,
    color: primaryColor,
    textDecoration: 'none',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
    marginTop: theme.spacing.lg,
    fontFamily: 'inherit',
  };

  const footerStyle: React.CSSProperties = {
    marginTop: theme.spacing.lg,
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.normal,
    color: theme.palette.text.white,
    opacity: 0.8,
  };

  const successMessageStyle: React.CSSProperties = {
    fontSize: theme.typography.fontSize.sm,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing.md,
  };

  const errorMessageStyle: React.CSSProperties = {
    fontSize: theme.typography.fontSize.sm,
    color: theme.palette.error.main,
    marginBottom: theme.spacing.md,
  };

  const requestForm = useForm<ForgotPasswordRequestValues>({
    defaultValues: { email: initialEmailValue },
    mode: 'onSubmit',
  });

  const resetForm = useForm<ForgotPasswordResetValues>({
    defaultValues: {
      token: initialToken?.trim() ?? '',
      newPassword: '',
      confirmPassword: '',
    },
    mode: 'onSubmit',
  });

  const onRequestSubmit = async (values: ForgotPasswordRequestValues) => {
    setSubmitError(null);
    setLoading(true);
    try {
      await onRequestToken(values.email.trim());
      setRequestSuccess(true);
      setStep('reset');
    } catch (err) {
      const message =
        err instanceof Error ? err.message : t('forgot_password.request_error');
      setSubmitError(message);
    } finally {
      setLoading(false);
    }
  };

  const onResetSubmit = async (values: ForgotPasswordResetValues) => {
    setSubmitError(null);
    setLoading(true);
    try {
      await onResetPassword({
        token: values.token.trim(),
        newPassword: values.newPassword,
      });
      setSubmitError(null);
      onBackToLogin();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : t('forgot_password.reset_error');
      setSubmitError(message);
    } finally {
      setLoading(false);
    }
  };

  const passwordRules = (value: string) => {
    if (!value || value.length < MIN_PASSWORD_LENGTH) {
      return t('forgot_password.validation_password_min', {
        count: MIN_PASSWORD_LENGTH,
      });
    }
    return true;
  };

  return (
    <div style={pageStyle} data-e2e="forgot-password-page">
      <Card style={cardContentStyle}>
        <div>
          <div style={logoStyle}>
            {typeof branding.logo !== 'string' || !branding.logo ? (
              branding.logo
            ) : (
              <img
                src={branding.logo}
                alt={t('login.logo_alt')}
                style={{
                  width: theme.layout.logoWidth,
                  height: theme.layout.logoHeight,
                }}
              />
            )}
          </div>
          <h1 style={titleStyle}>
            {step === 'email'
              ? t('forgot_password.title_request')
              : t('forgot_password.title_reset')}
          </h1>
          <p style={subtitleStyle}>
            {step === 'email'
              ? t('forgot_password.subtitle_request')
              : t('forgot_password.subtitle_reset')}
          </p>
        </div>

        {step === 'email' ? (
          <form
            style={formStyle}
            onSubmit={requestForm.handleSubmit(onRequestSubmit)}
            data-e2e="forgot-password-form-request"
          >
            {submitError && (
              <div
                style={errorMessageStyle}
                role="alert"
                data-e2e="forgot-password-error"
              >
                {submitError}
              </div>
            )}
            <Controller
              name="email"
              control={requestForm.control}
              rules={{
                required: t('login.validation_required'),
                validate: (value: string) =>
                  (value?.trim()?.length ?? 0) > 0 ||
                  t('login.validation_required'),
              }}
              render={({
                field,
                fieldState,
              }: {
                field: {
                  value: string;
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
                  onBlur: () => void;
                };
                fieldState: { error?: { message?: string } };
              }) => (
                <Input
                id="forgot-password-email"
                  titulo={t('login.email_label')}
                  type="email"
                  placeholder={t('login.email_placeholder')}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={fieldState.error?.message}
                data-e2e="forgot-password-email"
                />
              )}
            />
            <Button
              type="submit"
              size="large"
              style={{ width: '100%' }}
              backgroundColor={primaryColor}
              disabled={loading}
            data-e2e="forgot-password-submit"
            >
              {loading
                ? t('forgot_password.sending')
                : t('forgot_password.send_token')}
            </Button>
            <div
              style={{
                textAlign: 'center',
                width: '100%',
                marginTop: theme.spacing.md,
              }}
            >
              <button
                type="button"
                style={backLinkStyle}
                onClick={onBackToLogin}
                data-e2e="forgot-password-back-to-login"
              >
                {t('forgot_password.back_to_login')}
              </button>
            </div>
          </form>
        ) : (
          <form
            style={formStyle}
            onSubmit={resetForm.handleSubmit(onResetSubmit)}
            data-e2e="forgot-password-form-reset"
          >
            {requestSuccess && (
              <div
                style={successMessageStyle}
                role="status"
                data-e2e="forgot-password-success"
              >
                {t('forgot_password.token_sent')}
              </div>
            )}
            {submitError && (
              <div
                style={errorMessageStyle}
                role="alert"
                data-e2e="forgot-password-error"
              >
                {submitError}
              </div>
            )}
            <Controller
              name="token"
              control={resetForm.control}
              rules={{
                required: t('login.validation_required'),
                validate: (value: string) =>
                  (value?.trim()?.length ?? 0) > 0 ||
                  t('login.validation_required'),
              }}
              render={({
                field,
                fieldState,
              }: {
                field: {
                  value: string;
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
                  onBlur: () => void;
                };
                fieldState: { error?: { message?: string } };
              }) => (
                <Input
                id="forgot-password-token"
                  titulo={t('forgot_password.token_label')}
                  endAdornment={
                    <span
                      style={{
                        position: 'relative',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      onMouseEnter={() => setTokenTooltipVisible(true)}
                      onMouseLeave={() => setTokenTooltipVisible(false)}
                      aria-label={t('forgot_password.token_validity_tooltip')}
                    >
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          background: theme.palette.text.secondary,
                          color:
                            theme.palette.text?.white ??
                            theme.palette.background?.paper ??
                            theme.palette.background?.default,
                          fontSize: theme.typography.fontSize.sm,
                          fontWeight: theme.typography.fontWeight.semibold,
                          cursor: 'help',
                        }}
                      >
                        i
                      </span>
                      {tokenTooltipVisible && (
                        <span
                          role="tooltip"
                          style={{
                            position: 'absolute',
                            bottom: '100%',
                            left: '50%',
                            transform: 'translateX(-50%) translateY(-4px)',
                            padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                            background: theme.palette.text.primary,
                            color:
                              theme.palette.text?.white ??
                              theme.palette.background?.paper ??
                              theme.palette.background?.default,
                            fontSize: theme.typography.fontSize.sm,
                            fontWeight: theme.typography.fontWeight.normal,
                            borderRadius: theme.shape.borderRadius.md,
                            maxWidth: 220,
                            whiteSpace: 'normal',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            zIndex: 1000,
                            pointerEvents: 'none',
                          }}
                        >
                          {t('forgot_password.token_validity_tooltip')}
                        </span>
                      )}
                    </span>
                  }
                  type="text"
                  placeholder={t('forgot_password.token_placeholder')}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={fieldState.error?.message}
                  data-e2e="forgot-password-token"
                />
              )}
            />
            <Controller
              name="newPassword"
              control={resetForm.control}
              rules={{
                required: t('login.validation_required'),
                validate: passwordRules,
              }}
              render={({
                field,
                fieldState,
              }: {
                field: {
                  value: string;
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
                  onBlur: () => void;
                };
                fieldState: { error?: { message?: string } };
              }) => (
                <Input
                id="forgot-password-new-password"
                  titulo={t('forgot_password.new_password_label')}
                  type="password"
                  showPasswordToggle
                  passwordToggleAriaLabelShow={t(
                    'forgot_password.show_password'
                  )}
                  passwordToggleAriaLabelHide={t(
                    'forgot_password.hide_password'
                  )}
                  placeholder={t('forgot_password.new_password_placeholder')}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={fieldState.error?.message}
                  data-e2e="forgot-password-new-password"
                />
              )}
            />
            <Controller
              name="confirmPassword"
              control={resetForm.control}
              rules={{
                required: t('login.validation_required'),
                validate: (value: string) =>
                  value === resetForm.getValues('newPassword') ||
                  t('forgot_password.validation_confirm'),
              }}
              render={({
                field,
                fieldState,
              }: {
                field: {
                  value: string;
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
                  onBlur: () => void;
                };
                fieldState: { error?: { message?: string } };
              }) => (
                <Input
                id="forgot-password-confirm-password"
                  titulo={t('forgot_password.confirm_password_label')}
                  type="password"
                  showPasswordToggle
                  passwordToggleAriaLabelShow={t(
                    'forgot_password.show_password'
                  )}
                  passwordToggleAriaLabelHide={t(
                    'forgot_password.hide_password'
                  )}
                  placeholder={t(
                    'forgot_password.confirm_password_placeholder'
                  )}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={fieldState.error?.message}
                  data-e2e="forgot-password-confirm-password"
                />
              )}
            />
            <Button
              type="submit"
              size="large"
              style={{ width: '100%' }}
              backgroundColor={primaryColor}
              disabled={loading}
              data-e2e="forgot-password-reset-submit"
            >
              {loading
                ? t('forgot_password.resetting')
                : t('forgot_password.submit_reset')}
            </Button>
            <div
              style={{
                textAlign: 'center',
                width: '100%',
                marginTop: theme.spacing.md,
              }}
            >
              <button
                type="button"
                style={backLinkStyle}
                onClick={onBackToLogin}
                data-e2e="forgot-password-back-to-login"
              >
                {t('forgot_password.back_to_login')}
              </button>
            </div>
          </form>
        )}
      </Card>
      <footer style={footerStyle}>{branding.footerText}</footer>
    </div>
  );
}
